import React, { useState, useMemo, useEffect, memo } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { allTestimonials } from '@/data/testimonialsData';
import { ProgressiveImage } from './ProgressiveImage';

const TestimonialsSectionComponent = () => {
  // Helper to extract person identifier from testimonial
  const normalizePersonKey = (t: any): string => {
    // Try to extract from alt text (e.g., "Joy Frost - 45k viral")
    if (t.alt && t.alt.includes(' - ')) {
      return t.alt.split(' - ')[0].trim().toLowerCase();
    }
    // Fallback: extract from image filename
    const filename = t.image.split('/').pop()?.split('.')[0] || '';
    return filename.toLowerCase();
  };

  const arrangedTestimonials = React.useMemo(() => {
    // Start with priority-sorted testimonials
    const byPriority = [...allTestimonials].sort((a, b) => a.priority - b.priority);
    
    // Deduplicate by image
    const seenImages = new Set<string>();
    const dedupedByImage = byPriority.filter((t) => {
      if (seenImages.has(t.image)) return false;
      seenImages.add(t.image);
      return true;
    });

    // Spread by person: keep first occurrence, move duplicates to end
    const result: typeof allTestimonials = [];
    const buffer: typeof allTestimonials = [];
    const seenPersons = new Set<string>();

    dedupedByImage.forEach((t) => {
      const personKey = normalizePersonKey(t);
      if (seenPersons.has(personKey)) {
        buffer.push(t);
      } else {
        seenPersons.add(personKey);
        result.push(t);
      }
    });

    return [...result, ...buffer];
  }, []);

  // Ensure no person appears multiple times in the first 28 items
  const finalTestimonials = React.useMemo(() => {
    const INITIAL_LOAD = 28;
    const firstBatch = arrangedTestimonials.slice(0, INITIAL_LOAD);
    const rest = arrangedTestimonials.slice(INITIAL_LOAD);
    
    // Deduplicate first batch by person
    const seenPersonsInFirst = new Set<string>();
    const dedupedFirst: typeof allTestimonials = [];
    const pushedDown: typeof allTestimonials = [];
    
    firstBatch.forEach(t => {
      const person = normalizePersonKey(t);
      if (seenPersonsInFirst.has(person)) {
        pushedDown.push(t); // Move duplicate to after position 28
      } else {
        seenPersonsInFirst.add(person);
        dedupedFirst.push(t);
      }
    });
    
    return [...dedupedFirst, ...pushedDown, ...rest];
  }, [arrangedTestimonials]);

  const INITIAL_LOAD = 28;
  const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD);
  const itemsPerLoad = 12;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + itemsPerLoad);
  };

  const hasMore = visibleCount < finalTestimonials.length;
  const visibleTestimonials = finalTestimonials.slice(0, visibleCount);

  React.useEffect(() => {
    const counts = new Map<string, number>();
    allTestimonials.forEach((t) => counts.set(t.image, (counts.get(t.image) || 0) + 1));
    const dups = Array.from(counts.entries()).filter(([, n]) => n > 1);
    if (dups.length) console.warn('Duplicate testimonial images detected:', dups);
  }, []);

  return (
    <section id="testimonials" className="py-16 lg:py-20 bg-gray-50 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
            <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
              &gt; FROM ARTISTS
            </span>
          </div>
        </div>
        <h2 className="headline-lg text-gray-900 text-center mx-auto mb-3">
          Trusted by Artists{' '}
          <span className="inline-block bg-[#0071e3] text-white px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.5)]">
            Worldwide
          </span>
        </h2>
        <p className="body-large text-gray-600 text-center max-w-2xl mx-auto mb-6">
          Real messages from artists who've built momentum with our system
        </p>

        <div className="relative">
          <div className="columns-2 sm:columns-2 md:columns-3 lg:columns-4 gap-3 sm:gap-4" style={{ columnFill: 'balance' }}>
            {visibleTestimonials.map((testimonial, index) => {
            return (
              <Dialog key={testimonial.id}>
                <DialogTrigger asChild>
                  <div 
                    className="group relative overflow-hidden rounded-lg bg-white shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 hover:scale-[1.01] cursor-pointer break-inside-avoid mb-3 sm:mb-4"
                    style={{ willChange: 'auto' }}
                  >
                    <ProgressiveImage
                      src={testimonial.image}
                      alt={testimonial.alt}
                      loading={index < 16 ? "eager" : "lazy"}
                      priority={index < 8}
                    />
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] sm:max-w-xl max-h-[90vh] p-4 sm:p-2">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.alt}
                    className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                    loading="lazy"
                    decoding="async"
                  />
                </DialogContent>
              </Dialog>
            );
          })}
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-gray-50 via-gray-50/40 to-transparent pointer-events-none" />
        </div>

        {/* Show More Button */}
        {hasMore && (
          <div className="text-center mt-8">
            <button 
              onClick={handleLoadMore}
              className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-900 transition-colors duration-200 group text-sm"
              aria-label={`Show ${Math.min(itemsPerLoad, finalTestimonials.length - visibleCount)} more testimonials`}
            >
              <span>Show more</span>
              <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-200" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export const TestimonialsSection = memo(TestimonialsSectionComponent);
export default TestimonialsSection;
